import { useCallback, useSyncExternalStore } from 'react';
import {
  type ResolvedTheme,
  type Theme,
  applyThemePreference,
  getStoredTheme,
  initTheme,
  resolveTheme,
  setStoredTheme,
} from '@/lib/theme';

const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  for (const listener of listeners) listener();
}

function onSystemChange() {
  applyThemePreference(currentTheme);
  notify();
}

let currentTheme: Theme = initTheme(onSystemChange);

function getSnapshot(): Theme {
  return currentTheme;
}

function setTheme(theme: Theme) {
  currentTheme = theme;
  setStoredTheme(theme, onSystemChange);
  notify();
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, () => 'system');

  const resolvedTheme = useSyncExternalStore(
    subscribe,
    () => resolveTheme(currentTheme),
    (): ResolvedTheme => 'light',
  );

  const set = useCallback((next: Theme) => {
    setTheme(next);
  }, []);

  return { theme, resolvedTheme, setTheme: set };
}

/** Server / first paint: match inline script in index.html. */
export function readThemeFromStorage(): Theme {
  return getStoredTheme() ?? 'system';
}
